import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, User } from "../types/index";
import * as api from "../api/endpoints";

export const MIN_PASSWORD_LENGTH = 12;

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /**
   * Set only when the initial session check failed for a reason that is NOT "signed out".
   * A 401 is the ordinary signed-out state and leaves this null; a network failure or 5xx
   * means the API is unreachable, which otherwise looks identical to being logged out and
   * leaves the user clicking Login with no idea the backend is down.
   */
  unreachable: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    async function loadCurrentUser() {
      try {
        const current = await api.me();
        if (!cancelled) {
          setUser(current);
        }
      } catch (err) {
        // A 401 from /me simply means "signed out" — the normal state for a
        // visitor, not an error to surface. Anything else (network failure, 5xx)
        // means the API is unreachable, which is a different situation entirely:
        // it renders identically to being logged out, so flag it and let the
        // layout say so rather than silently showing a signed-out shell.
        if (!cancelled) {
          setUser(null);
          setUnreachable(!(err instanceof ApiError) || err.status !== 401);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    const loggedInUser = await api.login(email, password);
    setUser(loggedInUser);
    return loggedInUser;
  };

  const register = async (email: string, password: string): Promise<User> => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      throw new ApiError(
        422,
        "validation_error",
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
        "password"
      );
    }
    const registeredUser = await api.register(email, password);
    setUser(registeredUser);
    return registeredUser;
  };

  const logout = async (): Promise<void> => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      queryClient.clear();
    }
  };

  const value = useMemo(
    () => ({ user, loading, unreachable, login, register, logout }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, unreachable]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
